/* eslint-disable max-len */
import { Entity, SystemInterface } from '@goodboydigital/odie';

import { Settings } from '../../AppConfig';
import { Browser } from '../../utils/Browser';
import { BaseEntity } from '../entities/BaseEntity';
import { BillboardFactory } from '../entities/BillboardEntity';
import { CheckpointFactory } from '../entities/CheckpointEntity';
import { CoinFactory } from '../entities/CoinEntity';
import { FlagEndFactory } from '../entities/FlagEndEntity';
import { FlagStartFactory } from '../entities/FlagStartEntity';
import { FlipperFactory } from '../entities/FlipperEntity';
import { GroundFactory } from '../entities/GroundEntity';
import { LauncherFactory } from '../entities/LauncherEntity';
import { NativeAdPanelFactory } from '../entities/NativeAdPanelEntity';
import { PuncherFactory } from '../entities/PuncherEntity';
import { RectEntity } from '../entities/RectEntity';
import { SeeSawFactory } from '../entities/SeeSawEntity';
import { SegmentFactory } from '../entities/SegmentEntity';
import { ShapeFactory } from '../entities/ShapeEntity';
import { TutorialAreaFactory } from '../entities/TutorialAreaEntity';
import { UndertrackFactory } from '../entities/UndertrackEntity';
import { WarningSignFactory } from '../entities/WarningSignEntity';
import { WindmillFactory } from '../entities/WindmillEntity';
import { WreckingBallFactory } from '../entities/WreckingBallEntity';
import { Game } from '../Game';
import { PhysicsCollisionGroup } from '../physics';
import { SpineDebugSystem } from './SpineDebugSystem';

export interface LevelData
{
    name: string,
    scenario: string,
    chunkSequence: string[]
}

export interface LevelEntityParams
{
    type: string,
    x: number,
    y: number,
    w: number,
    h: number,
    sx: number,
    sy: number,
    r: number,
    img: string,
}

/**
 * Function to match and create entities, done this way because entity creation is not always 1:1, for example,
 * A segment data from chunk matched with GroundEntity will place not only the GroundEntity but several sprite entities for
 * decoration. SO each entity should have factory functions that will handle instantiation.
 */
export interface EntityFactory
{
    match: (data: any) => boolean;
    create: (game: Game, data: any) => void
}

export class LevelSystem implements SystemInterface
{
    /**
     * Registered entity factories that level system should use.
     */
    public factories: EntityFactory[] = [
        GroundFactory,
        WindmillFactory,
        LauncherFactory,
        CoinFactory,
        FlagStartFactory,
        FlagEndFactory,
        SeeSawFactory,
        FlipperFactory,
        PuncherFactory,
        WreckingBallFactory,
        WarningSignFactory,
        BillboardFactory,
        NativeAdPanelFactory,
        CheckpointFactory,
        UndertrackFactory,
        ShapeFactory,
        SegmentFactory,
        TutorialAreaFactory
    ];

    /**
     * Define the 'gameplay' version, which is not tied to the app version
     * Different versions of the app can have the same gameplay version, and that means
     * replays form those different versions are interchangeable, because nothing has changed gameplay-wise
     *
     * Bump this version only in case entities shapes and/or behaviours has changed, and that will invalidate
     * replays played on different versions than this one.
     *
     * There is no need to bump this in case of changes in chunks or level data - these things are handled
     * by the levelHash, and replays can get invalidated even if they are in the same version
     *
     * To summarise, app will match `LEVEL_SYSTEM_VERSION` and `levelHash` to see if a certain replay
     * can be played or not in the current game.
     */
    public readonly LEVEL_SYSTEM_VERSION = 7;

    public levelName = '';

    public game: Game;
    public target?: BaseEntity;

    private groundOffsetX = 0;
    private groundOffsetY = 0;
    private groundPosX = 0;
    private groundPosY = 0;

    public courseStartX = 300;
    public courseStartY = -300;
    public courseEndX = 0;
    public courseEndY = 0;
    public levelMinX = 0;
    public levelMaxX = 0;
    public entities: BaseEntity[];
    public chunks: string[];
    public segments = 0;
    public segmentsLength = 0;
    public idle = true;

    private leftWall?: RectEntity;
    private rightWall?: RectEntity;

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
        this.entities = [];
        this.chunks = [];
        this.game.physics.physicsFixedUpdateRunner.add(this as any);
    }

    public addEntity(entity: Entity, container?: BaseEntity): void
    {
        const en = entity as BaseEntity;

        if (!this.entities.includes(en))
        {
            this.entities.push(en);
            this.game.addToScene(en);
            if (container) container.view.addChild(en.view);
        }
    }

    public removeEntity(entity: Entity): void
    {
        const en = entity as BaseEntity;

        if (this.entities.includes(en))
        {
            this.entities.splice(this.entities.indexOf(en), 1);
            this.game.removeFromScene(en);
            if (en.view.parent) en.view.parent.removeChild(en.view);
        }
    }

    /** Called when level restart, like 'play again' */
    public levelReset(): void
    {
        for (const en of this.game.allEntities.children)
        {
            (en as any).levelReset?.(this.courseStartX, this.courseStartY);
        }
    }

    /** Called when player respawn, after a crash */
    public levelRespawn(): void
    {
        for (const en of this.game.allEntities.children)
        {
            (en as any).levelRespawn?.(this.courseStartX, this.courseStartY);
        }
    }

    /**
     * Clean up all entities added to the current level and reset all parameters
     */
    public levelClear(): void
    {
        let i = this.entities.length;

        while (i--)
        {
            const en = this.entities[i];

            (en as any).levelReset?.(this.courseStartX, this.courseStartY);

            this.game.removeChild(en);
            this.game.pool.return(en);
            en.destroy();
        }

        this.groundOffsetX = 0;
        this.groundOffsetY = 0;
        this.groundPosX = 0;
        this.groundPosY = 0;
        this.courseStartX = 99999;
        this.courseStartY = 0;
        this.courseEndX = 99999;
        this.courseEndY = 0;
        this.segments = 0;
        this.segmentsLength = 0;
        this.entities = [];
        this.chunks = ['default-idle'];
        this.levelName = '';
        this.levelMinX = 0;
        this.levelMaxX = 0;

        if (this.leftWall) this.game.physics.ensurePartsRemoved(this.leftWall.physics);
        if (this.rightWall) this.game.physics.ensurePartsRemoved(this.rightWall.physics);
        this.leftWall = undefined;
        this.rightWall = undefined;
    }
    
    public countNormalLevels(): number
    {
        const re = /level\-0(.*?)/;
        
        return this.game.app.assets.searchData(re).length;
    }

    public countRandomLevels(): number
    {
        const re = /level\-random\-(.*?)/;

        return this.game.app.assets.searchData(re).length;
    }

    private getLevelNameByIndex(index: number): string
    {
        if (index === 0) return 'tutorial';

        // Try to use one of the default levels
        // They are named like this: 'level-001.json', 'level-002.json'
        const regularLevelName = String(index).padStart(3, '0');

        if (this.game.app.assets.hasData(`level-${regularLevelName}`))
        {
            return regularLevelName;
        }

        // Otherwise, use one of the random generated levels
        // They are named like this: 'level-random-001.json', 'level-random-002.json'
        const randomLevels = this.countRandomLevels();
        const randomLevelIndex = ((index - 1) % randomLevels) + 1;
        const randomLevelName = `random-${String(randomLevelIndex).padStart(3, '0')}`;

        if (this.game.app.assets.hasData(`level-${randomLevelName}`))
        {
            return randomLevelName;
        }

        // If index didn't match any of the existing levels data must be missing in assets folder
        throw new Error(`No level found for index: ${index}`);
    }
    
    public countChunkCountInLevels(): void
    {
        var chunkCount: Record<string, any> = {};
        console.log(this.countNormalLevels())
        for(var i = 0; i<this.countNormalLevels(); i++){
            const regularLevelName = String(i+1).padStart(3, '0');
            const data = this.game.app.data.getLevelData(`${regularLevelName}`);
            for (const chunk of data.chunkSequence)
            {
                if(!chunkCount[chunk]){
                    chunkCount[chunk] = 1;
                }
                else{
                    chunkCount[chunk]++;
                }
            }
        }
        
        for(var i = 0; i<this.countRandomLevels(); i++){
            const randomLevelName = `random-${String(i+1).padStart(3, '0')}`;
            const data = this.game.app.data.getLevelData(`${randomLevelName}`);
            for (const chunk of data.chunkSequence)
            {
                if(!chunkCount[chunk]){
                    chunkCount[chunk] = 1;
                }
                else{
                    chunkCount[chunk]++;
                }
            }
        }
    }

    /**
     * Put the level system in idle mode, for the title screen
     */
    public mountIdleLevel(): void
    {
        this.idle = true;
        this.mountChunk('default-wave', false);
        this.updateWalls();
    }

    /**
     * Mount a level based on a race index. If race index is 1, then 'level-001' will be built.
     * If there is no correspondent level for given race number, then the level system will use
     * 'race-random' instead, with a seed per race index. Mind that the seed will 'loop' after every
     * [RANDOM_LEVELS]th race.
     * @param raceIndex - The index of the race to be mounted
     */
    public mountLevelForRace(raceIndex: number): void
    {
        const levelName = Settings.level || this.getLevelNameByIndex(raceIndex);
        console.log(Settings.level, levelName)
        this.mountLevel(levelName);
    }

    /**
     * Mount a level by level name. If level name is 'default' that will try to find
     * a file named 'level-default.json' among assets data files
     * @param levelName - Name of the level to be mounted.
     * @param seed - Random seed for mouting the level. Same level name and seed will always produce the same result.
     */
    private mountLevel(levelName: string): void
    {
        console.log('[LevelSystem] ===== MOUNT LEVEL START:', levelName);
        this.idle = false;
    
        const data = this.game.app.data.getLevelData(levelName);
        
        this.mountLevelScenario(data.scenario);

        if (Settings.chunk)
        {
            // Replace chunk sequence by testing chunk
            data.chunkSequence = ['default-start', Settings.chunk, 'default-end'];
            if (Settings.chunk === 'none') data.chunkSequence.splice(1, 1);
        }

        this.levelName = levelName;

        if (levelName === 'tutorial') this.game.tutorial.setupTutorial();

        for (const chunk of data.chunkSequence)
        {
            this.mountChunk(chunk);
        }

        this.mountChunk('default-idle', false);
        this.mountChunk('default-idle', false);
        this.levelMaxX = this.groundPosX;

        this.updateWalls();
        this.levelReset();

        // Force a render step to ensure all entities positions updated
        this.game.view25d.render();

        console.log('[LevelSystem] Num physics bodies:', this.game.physics.countBodies());
        console.log('[LevelSystem] Num physics shapes:', this.game.physics.countShapes());
        console.log('[LevelSystem] Num entities:', this.game.allEntities.length);
        console.log('[LevelSystem] ===== MOUNT LEVEL COMPLETE');
    }
    
    private mountLevelScenario(scenario: string): void
    {
        scenario = scenario !== undefined ? scenario : "city";
        //Change scenario if level demands it, requires resetting some things
        if(this.game.scenario.mounted != scenario){
            this.game.scenario.mountScenario(scenario);
            this.levelClear();
            this.mountIdleLevel();
            this.mountIdleLevel();
            
            this.levelMinX += 1024;
            if (this.leftWall) this.game.physics.ensurePartsRemoved(this.leftWall.physics);
            if (this.rightWall) this.game.physics.ensurePartsRemoved(this.rightWall.physics);
            
            this.leftWall = undefined;
            this.rightWall = undefined;
            this.updateWalls();
            
            this.idle = false;
            
            this.game.vehicle.reset();
            this.game.vehicle.respawn(1540, -60, 0, false);
            this.game.camera.resetTarget(this.game.vehicle.character.physics, 0.06);
        }
    }

    /**
     * Mount a chunk in current level
     * @param chunkName - Name of the chunk to be mounted
     * @param listedForLevelHash - Tell if this chunk should be considered in the level hash generation
     */
    public mountChunk(chunkName: string, listedForLevelHash = true): void
    {
        console.log('[LevelSystem] Mount chunk:', chunkName);

        // Add chunks that will affect level hash only
        if (listedForLevelHash) this.chunks.push(chunkName);

        const data = this.game.chunks.getChunk(chunkName);

        this.groundOffsetX = data.info.groundStartX;
        this.groundOffsetY = data.info.groundStartY;

        for (const entityData of data.entities)
        {
            for (const factory of this.factories)
            {
                
                if (factory.match(entityData))
                {
                    factory.create(this.game, {
                        ...entityData,
                        x: entityData.x + this.groundPosX - this.groundOffsetX,
                        y: entityData.y + this.groundPosY - this.groundOffsetY,
                    });
                    break;
                }
            }
        }
        this.groundPosX += (data.info.groundEndX - data.info.groundStartX);
        this.groundPosY += (data.info.groundEndY - data.info.groundStartY);
        this.segments += data.info.segments;
    }

    /** Update left and right invisible walls, as level boudaries */
    private updateWalls(): void
    {
        if (!this.leftWall)
        {
            this.leftWall = new RectEntity();
            this.leftWall.build({
                w: 32, h: 2048,
                mass: 0,
                group: PhysicsCollisionGroup.OBJECT,
                mask: PhysicsCollisionGroup.VEHICLE,
            });
            this.game.addChild(this.leftWall);
        }

        if (!this.rightWall)
        {
            this.rightWall = new RectEntity();
            this.rightWall.build({
                w: 32, h: 2048,
                mass: 0,
                group: PhysicsCollisionGroup.OBJECT,
                mask: PhysicsCollisionGroup.VEHICLE,
            });
            this.game.addChild(this.rightWall);
        }

        this.leftWall.physics.setPosition(this.levelMinX, -1024);
        this.rightWall.physics.setPosition(this.levelMaxX, this.groundPosY - 1024);
        this.game.physics.ensurePartsAdded(this.leftWall.physics);
        this.game.physics.ensurePartsAdded(this.rightWall.physics);
    }

    /** Get current level hash, based on its current chunk sequence */
    public getLevelHash(): string
    {
        return this.getLevelHashSimple();
    }

    /** Simple hash based only on current chunk sequence, changes inside chunks will not change this */
    protected getLevelHashSimple(): string
    {
        const str = JSON.stringify(this.chunks) + this.LEVEL_SYSTEM_VERSION;
        const hash = Browser.sha256(str);

        return hash;
    }

    /** Very strict level hah, based on actual chunk data - protect to any changes in chunks */
    protected getLevelHashStrict(): string
    {
        const chunkList = this.chunks.slice(0);
        const levelData = this.game.app.data.getLevelData(this.levelName);
        const chunks = chunkList.map((name: string) => this.game.app.data.getChunkData(name));
        const str = JSON.stringify(levelData) + JSON.stringify(chunks) + this.LEVEL_SYSTEM_VERSION;
        const hash = Browser.sha256(str);

        return hash;
    }

    /**
     * Move the left wall right before the starting line,
     * to prevent players from going back too far once race starts
     */
    public limitMovementToStartingPoint(): void
    {
        this.levelMinX = this.courseStartX - 200;
        if (this.leftWall) this.game.physics.ensurePartsRemoved(this.leftWall.physics);
        if (this.rightWall) this.game.physics.ensurePartsRemoved(this.rightWall.physics);
        this.leftWall = undefined;
        this.rightWall = undefined;
        this.updateWalls();
    }

    /** Remove entities that are too far from given point. Used only in idle mode */
    private removePastEntities(minX: number): void
    {
        let i = this.entities.length;

        while (i--)
        {
            const en = this.entities[i] as any;
            const x = en.physics ? en.physics.x : en.x;

            if (x > minX - 1000) continue;

            this.game.removeChild(en);
            this.game.pool.return(en);
            this.entities.splice(i, 1);
        }

        if (this.leftWall) this.game.physics.ensurePartsRemoved(this.leftWall.physics);
        if (this.rightWall) this.game.physics.ensurePartsRemoved(this.rightWall.physics);
        this.leftWall = undefined;
        this.rightWall = undefined;
        this.levelMinX = minX;
        this.updateWalls();
    }

    /** Get the minimum X of current level, based on left wall */
    private getLevelMinX(): number
    {
        return this.leftWall ? this.leftWall.physics.x : 0;
    }

    public update(): void
    {
        if (this.idle)
        {
            const offset = (this.game.spineDebug?.vehicles.length ?? 0) * SpineDebugSystem.GAP;

            if (this.game.vehicle.chassis.physics.x > this.groundPosX - (600 + offset))
            {
                this.mountChunk('default-wave', false);
            }

            if (this.game.vehicle.chassis.physics.x > this.getLevelMinX() + 1400)
            {
                this.removePastEntities(this.getLevelMinX() + 1000);
            }
        }
    }

    public getProfile(): Record<string, any>
    {
        return {
            level: this.levelName,
            entities: this.entities.length,
            segments: this.segments,
            chunks: `${this.chunks.length} - ${this.chunks.join(', ')}`,
        };
    }
}
